<template>
  <div>
    <v-img
      v-if="imageSource"
      ref="cameraImage"
      :src="imageSource"
      :lazy-src="greyImg"
      :width="width"
      :max-width="maxWidth || width"
      :height="height"
      :max-height="maxHeight || height"
      :aspect-ratio="aspectRatio || 1"
      class="rounded-image mr-2"
    ></v-img>
    <div v-else class="offline-camera-wrapper rounded-image mr-2" :style="offlineCameraWrapperStyle">
      <div class="offline-camera" :style="offlineCameraStyle"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CameraImage",
  props: ["camera", "width", "maxWidth", "height", "maxHeight", "aspectRatio"],
  data() {
    return {
      visible: false,
      observer: null,
      snapshotRequestPending: false,
      SNAPSHOT_MAX_AGE_MS: 1800000
    };
  },
  computed: {
    imageSource() {
      const s =
        this.camera && this.camera.hasOwnProperty("snapshots")
          ? this.camera.snapshots.find((s) => {
              return s.resolution === this.resolution;
            })
          : null;
      return s && s.image ? s.image : this.lazySrc;
    },
    resolution() {
      return `1000x100`; // this will get the correct width for desired height of 100
    },
    offlineCameraWrapperStyle() {
      return {
        width: `${this.width}px`,
        height: `${this.height}px`,
      };
    },
    offlineCameraStyle() {
      return {
        left: `${this.width / 2 + 4}px`,
        top: `${this.height / 2 - 10}px`,
      };
    },
  },
  watch: {
    "camera.status": function (newVal, oldVal) {
      this.updateSnapshot();
    },
  },
  created() {
    if (!this.camera.hasOwnProperty("snapshots")) this.$set(this.camera, "snapshots", []);
  },
  mounted() {
    this.observer = new IntersectionObserver(([el]) => {
      if (!this.visible) this.visible = el.intersectionRatio > 0;
      if (el.intersectionRatio > 0) {
        this.updateSnapshot();
      }
    }, {});

    this.observer.observe(this.$el);
  },
  destroyed() {
    this.observer.disconnect();
  },
  methods: {
    async updateSnapshot() {
      const statusUnknown = (!this.camera.status) // to start fetching snapshots before the camera status is known
      const statusOnline = (this.camera.status && this.camera.status.online);
      if (this.visible && !this.snapshotRequestPending && (statusOnline || statusUnknown)) {
        const snapshot = this.camera.snapshots.find((s) => {
          return s.resolution === this.resolution;
        });
        // do not update the snapshot if we have one already, unless it's outdated
        if (snapshot && snapshot.time && Date.now() - snapshot.time < this.SNAPSHOT_MAX_AGE_MS) {
          console.debug("Snapshot is fresh, not requesting new one", this.camera.cameraId);
          return;
        }

        this.snapshotRequestPending = true;
        try {
          const snapshot = await this.restapi.getSnapshot(this.camera.cameraId, this.resolution);
          const i = this.camera.snapshots.findIndex((s) => {
            return s.resolution === this.resolution;
          });
          if (i !== -1) {
            this.$set(this.camera.snapshots, i, { resolution: `${this.resolution}`, image: snapshot, time: Date.now() });
          } else {
            this.camera.snapshots.push({ resolution: `${this.resolution}`, image: snapshot, time: Date.now() });
          }
        } catch (error) {
          console.error("Error fetching snapshot:", error);
        } finally {
          this.snapshotRequestPending = false;
        }
      }
    },
  }
};
</script>

<style lang="scss" scoped>
@import "../../assets/styles/main";

.rounded-image {
  border-radius: $double-border-radius;
}
.offline-camera-wrapper {
  background-color: #959595;
  .offline-camera {
    width: 100%;
    height: 100%;
    background-color: white;
    mask: url("~@eencloud/core-components/src/assets/images/videocam_off.svg") no-repeat;
    position: absolute;
  }
}
</style>
